import { useEffect, useState, type VFC } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { renderGenomeAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";

import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { type InputTableQuerySpec } from "../../graphql.js";
import { type IGenomeState, shouldLoadGenome } from "../../redux/modules/genome.js";
import { createTableSpecForOrganismParent } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { ENTITY_TYPE } from "../../settings/settings.js";
import GenericDataTableContainer from "../core/grid/GenericDataTableContainer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { ChildEntitiesButtonHeader } from "../core/navigation-panel/ButtonHeaderHoc.js";

interface IGenomeRelatedEntitiesViewOwnProps {
  subtitle: string;
  relatedEntitiesType: string;
  relatedEntitiesSubType?: number;
  hideCheckboxes?: boolean;
  middleToolbarElement?: React.ReactNode;
  actionButtons?: React.ReactNode;
}

const GenomeRelatedEntitiesView: VFC<IGenomeRelatedEntitiesViewOwnProps> = (props) => {
  const [tableSpec, setTableSpec] = useState<InputTableQuerySpec>();
  const { genomeId } = useParams<"genomeId">();
  const viewId = useURLViewID();
  const { genome } = useSelector<IAppState, IGenomeState>((state) => state.genome);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadGenome, genomeId);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (genome && props.relatedEntitiesType) {
      setTableSpec(
        createTableSpecForOrganismParent(
          props.relatedEntitiesType,
          genome.id,
          genome.isReference && genome.id,
          props.relatedEntitiesSubType,
        ),
      );
    }
  }, [genome, props.relatedEntitiesType, props.relatedEntitiesSubType]);

  return (
    <GenericDataTableContainer
      title={genome?.name ?? ""}
      subtitle={props.subtitle}
      tableSpec={tableSpec}
      middleToolbarElement={props.middleToolbarElement}
      rightToolbarElement={[
        props.actionButtons,
        <ChildEntitiesButtonHeader
          key={"child-entities-button-header"}
          tableSpec={tableSpec}
          getParentEntityName={() => genome?.name ?? ""}
          hideAnalyzeBtn={
            props.relatedEntitiesType === ENTITY_TYPE.MARKER || props.relatedEntitiesType === ENTITY_TYPE.COORD_LIST
          }
        />,
      ]}
      viewId={viewId ?? undefined}
      hideCheckboxes={props.hideCheckboxes}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderGenomeAttributesWidgets(genome)}
          key={"genome-related-entities-view-widget-1"}
        />,
      ]}
      genomeSequencingType={genome?.sequencingType ?? undefined}
    />
  );
};

export default GenomeRelatedEntitiesView;
